import React, { useState } from "react";
import CancelIcon from "@mui/icons-material/Cancel";

const Calculator = ({ handleClose }) => {
  const [display, setDisplay] = useState("0");
  const [expression, setExpression] = useState("");
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [resetDisplay, setResetDisplay] = useState(false);
  const [calculated, setCalculated] = useState(false);

  const handleNumberClick = (number) => {
    if (calculated) {
      setExpression("");
      setCalculated(false);
    }

    if (display === "0" || resetDisplay) {
      setDisplay(number);
      setResetDisplay(false);
    } else {
      setDisplay(display + number);
    }
  };

  const handleOperationClick = (op) => {
    const currentValue = display;

    if (calculated) {
      setExpression(display);
      setCalculated(false);
    }

    if (previousValue === null) {
      setExpression(
        expression
          ? expression + " " + currentValue + " " + op
          : currentValue + " " + op
      );
    } else if (operation) {
      // If there's already an operation, replace it
      setExpression(expression.slice(0, -1) + op);
    }

    setPreviousValue(parseFloat(display));
    setOperation(op);
    setResetDisplay(true);
  };

  const handleEquals = () => {
    if (!operation || previousValue === null) return;

    const currentValue = parseFloat(display);
    let result = 0;

    switch (operation) {
      case "+":
        result = previousValue + currentValue;
        break;
      case "-":
        result = previousValue - currentValue;
        break;
      case "×":
        result = previousValue * currentValue;
        break;
      case "÷":
        result = previousValue / currentValue;
        break;
      default:
        return;
    }

    const newExpression = expression + " " + display + " = " + result;
    setExpression(newExpression);
    setDisplay(String(result));
    setPreviousValue(null);
    setOperation(null);
    setCalculated(true);
  };

  const handleClear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperation(null);
    setResetDisplay(false);
    setExpression("");
    setCalculated(false);
  };

  const handlePercentage = () => {
    const percentValue = parseFloat(display) / 100;
    setDisplay(String(percentValue));

    if (expression && !calculated) {
      setExpression(expression + " " + display + "% = " + percentValue);
    } else {
      setExpression(display + "% = " + percentValue);
    }
  };

  const handlePlusMinus = () => {
    const negatedValue = -parseFloat(display);
    setDisplay(String(negatedValue));
  };

  const buttonClasses = {
    light: "bg-gray-300 text-black hover:bg-gray-200 font-medium",
    operation: "bg-orange-500 text-white hover:bg-orange-400 font-medium",
    dark: "bg-gray-700 text-white hover:bg-gray-600 font-medium",
  };

  return (
    <div
      // onClick={() => handleClose(false)}
      className="absolute w-full h-full inset-0  bg-black bg-opacity-50"
    >
      <div className=" ml-5 mt-5 bg-white absolute z-50 p-4 rounded-3xl max-w-xs mx-auto shadow-lg ">
        <div className="flex justify-end mb-2">
          <CancelIcon
            onClick={() => handleClose(false)}
            className="cursor-pointer"
          />
        </div>
        {/* Expression Display */}
        <div className="text-right p-2 bg-gray-800 rounded-lg mb-1 h-12 overflow-x-auto">
          <div className="text-gray-300 text-lg font-light whitespace-nowrap">
            {expression}
          </div>
        </div>

        {/* Current Number Display */}
        <div className="text-right p-4 mb-2 bg-gray-900 rounded-lg">
          <div className="text-white text-4xl font-light truncate">
            {display}
          </div>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-4 gap-3">
          <button
            onClick={handleClear}
            className={`${buttonClasses.light} h-16 w-16 rounded-full text-xl`}
          >
            {previousValue === null ? "AC" : "C"}
          </button>
          <button
            onClick={handlePlusMinus}
            className={`${buttonClasses.light} h-16 w-16 rounded-full text-xl`}
          >
            +/-
          </button>
          <button
            onClick={handlePercentage}
            className={`${buttonClasses.light} h-16 w-16 rounded-full text-xl`}
          >
            %
          </button>
          <button
            onClick={() => handleOperationClick("÷")}
            className={`${buttonClasses.operation} h-16 w-16 rounded-full text-2xl`}
          >
            ÷
          </button>

          <button
            onClick={() => handleNumberClick("7")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            7
          </button>
          <button
            onClick={() => handleNumberClick("8")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            8
          </button>
          <button
            onClick={() => handleNumberClick("9")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            9
          </button>
          <button
            onClick={() => handleOperationClick("×")}
            className={`${buttonClasses.operation} h-16 w-16 rounded-full text-2xl`}
          >
            ×
          </button>

          <button
            onClick={() => handleNumberClick("4")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            4
          </button>
          <button
            onClick={() => handleNumberClick("5")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            5
          </button>
          <button
            onClick={() => handleNumberClick("6")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            6
          </button>
          <button
            onClick={() => handleOperationClick("-")}
            className={`${buttonClasses.operation} h-16 w-16 rounded-full text-2xl`}
          >
            -
          </button>

          <button
            onClick={() => handleNumberClick("1")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            1
          </button>
          <button
            onClick={() => handleNumberClick("2")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            2
          </button>
          <button
            onClick={() => handleNumberClick("3")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            3
          </button>
          <button
            onClick={() => handleOperationClick("+")}
            className={`${buttonClasses.operation} h-16 w-16 rounded-full text-2xl`}
          >
            +
          </button>

          <button
            onClick={() => handleNumberClick("0")}
            className={`${buttonClasses.dark} col-span-2 h-16 rounded-full text-2xl text-left pl-6`}
          >
            0
          </button>
          <button
            onClick={() => handleNumberClick(".")}
            className={`${buttonClasses.dark} h-16 w-16 rounded-full text-2xl`}
          >
            .
          </button>
          <button
            onClick={handleEquals}
            className={`${buttonClasses.operation} h-16 w-16 rounded-full text-2xl`}
          >
            =
          </button>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
